import { getApiError } from '../lib/apiError';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  ShoppingCart, Plus, Minus, Trash2, Search, CreditCard, 
  Banknote, QrCode, Receipt, Calculator, User, Printer,
  ArrowDownCircle, ArrowUpCircle, Wallet, History, FileText, Import, Check, UserCheck,
  FileCode, Loader2, X, SplitSquareHorizontal, Percent, Volume2, VolumeX, Bell, Pencil
} from 'lucide-react';
import { ReceiptPrint, DANFEPrint } from '../components/ReceiptPrint';
import { checkAndCelebrateDailyGoal } from '../components/GoalCelebration';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PDV = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [orderSearchTerm, setOrderSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('dinheiro');
  const [amountPaid, setAmountPaid] = useState('');
  const [discount, setDiscount] = useState(0);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);
  const [showDocumentChoiceDialog, setShowDocumentChoiceDialog] = useState(false);
  const [showDANFEDialog, setShowDANFEDialog] = useState(false);
  const [showCashMovementDialog, setShowCashMovementDialog] = useState(false);
  const [showCashHistoryDialog, setShowCashHistoryDialog] = useState(false);
  const [lastSale, setLastSale] = useState(null);
  const [lastNfce, setLastNfce] = useState(null);
  const [nfceConfig, setNfceConfig] = useState(null);
  const [emittingNfce, setEmittingNfce] = useState(false);
  const [todaySummary, setTodaySummary] = useState({ total_sales: 0, total_count: 0 });
  const [cashBalance, setCashBalance] = useState({ cash_balance: 0, total_sangria: 0, total_suprimento: 0, cash_sales: 0 });
  const [cashMovements, setCashMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [movementType, setMovementType] = useState('sangria');
  const [movementForm, setMovementForm] = useState({ amount: '', reason: '', notes: '' });
  const [showEditMovementDialog, setShowEditMovementDialog] = useState(false);
  const [editingMovement, setEditingMovement] = useState(null);
  const [editMovementForm, setEditMovementForm] = useState({ type: 'sangria', amount: '', reason: '', notes: '' });
  const searchRef = useRef(null);
  const orderSearchRef = useRef(null);
  const [pendingItems, setPendingItems] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [newOrderAlert, setNewOrderAlert] = useState(false);
  
  // Refs para controle de som
  const previousPendingCountRef = useRef(0);
  const audioContextRef = useRef(null);
  const isFirstLoadRef = useRef(true);
  
  // Estados para múltiplas formas de pagamento
  const [useMultiplePayments, setUseMultiplePayments] = useState(false);
  const [paymentsList, setPaymentsList] = useState([]);
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState('dinheiro');
  const [currentPaymentAmount, setCurrentPaymentAmount] = useState('');
  
  // Estado para dados da empresa (inclui logo)
  const [companyData, setCompanyData] = useState({ name: '', logo: '' });

  // Função para criar e tocar som de notificação
  const playNotificationSound = () => {
    if (!soundEnabled) return;
    
    try {
      // Cria AudioContext se não existir
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const ctx = audioContextRef.current;
      
      // Cria um som de "ding" agradável
      const oscillator1 = ctx.createOscillator();
      const oscillator2 = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator1.connect(gainNode);
      oscillator2.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      // Frequências para criar um som de "ding-dong"
      oscillator1.frequency.setValueAtTime(880, ctx.currentTime); // A5
      oscillator1.frequency.setValueAtTime(660, ctx.currentTime + 0.15); // E5
      
      oscillator2.frequency.setValueAtTime(1320, ctx.currentTime); // E6
      oscillator2.frequency.setValueAtTime(880, ctx.currentTime + 0.15); // A5
      
      oscillator1.type = 'sine';
      oscillator2.type = 'sine';
      
      // Envelope do volume
      gainNode.gain.setValueAtTime(0, ctx.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 0.02);
      gainNode.gain.linearRampToValueAtTime(0.2, ctx.currentTime + 0.15);
      gainNode.gain.linearRampToValueAtTime(0.15, ctx.currentTime + 0.3);
      gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.5);
      
      oscillator1.start(ctx.currentTime);
      oscillator2.start(ctx.currentTime);
      oscillator1.stop(ctx.currentTime + 0.5);
      oscillator2.stop(ctx.currentTime + 0.5);
      
      // Mostra alerta visual
      setNewOrderAlert(true);
      setTimeout(() => setNewOrderAlert(false), 3000);
      
    } catch (error) {
      console.log('Audio not supported:', error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchPendingItems();
    searchRef.current?.focus();
    
    // Atualiza pendentes a cada 15 segundos (mais frequente para detectar novos pedidos)
    const interval = setInterval(fetchPendingItems, 15000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, clientsRes, summaryRes, balanceRes, sellersRes, nfceRes, companyRes] = await Promise.all([
        axios.get(`${API_URL}/api/products`),
        axios.get(`${API_URL}/api/clients`),
        axios.get(`${API_URL}/api/pdv/summary`),
        axios.get(`${API_URL}/api/pdv/cash-balance`),
        axios.get(`${API_URL}/api/sellers`),
        axios.get(`${API_URL}/api/nfce/config`).catch(() => ({ data: { configured: false } })),
        axios.get(`${API_URL}/api/company`).catch(() => ({ data: { name: '', logo: '' } }))
      ]);
      setProducts(productsRes.data);
      setClients(clientsRes.data);
      setTodaySummary(summaryRes.data);
      setCashBalance(balanceRes.data);
      setSellers(sellersRes.data);
      setNfceConfig(nfceRes.data.configured ? nfceRes.data : null);
      setCompanyData(companyRes.data);
      // Define o usuário logado como vendedor padrão
      if (!selectedSeller && user) {
        setSelectedSeller({ id: user.id, name: user.name });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchPendingItems = async () => {
    try {
      const [ordersRes, salesRes] = await Promise.all([
        axios.get(`${API_URL}/api/pdv/orders/search`),
        axios.get(`${API_URL}/api/pdv/sales/search`)
      ]);
      
      const orders = ordersRes.data.map(o => ({ ...o, _type: 'order' }));
      const sales = salesRes.data.map(s => ({ ...s, _type: 'sale' }));
      
      // Ordena por data (mais recentes primeiro)
      const combined = [...orders, ...sales].sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      
      // Detecta se há novos pedidos (não no primeiro carregamento)
      const currentCount = combined.length;
      if (!isFirstLoadRef.current && currentCount > previousPendingCountRef.current) {
        const newCount = currentCount - previousPendingCountRef.current;
        playNotificationSound();
        toast.info(`🔔 ${newCount} novo(s) pedido(s) pendente(s)!`, {
          duration: 5000,
          position: 'top-center'
        });
      }
      
      // Atualiza a referência
      previousPendingCountRef.current = currentCount;
      isFirstLoadRef.current = false;
      
      setPendingItems(combined);
    } catch (error) {
      console.error('Error fetching pending items:', error);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const filteredProducts = products.filter(p => 
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.ean?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const addToCart = (product) => {
    if (product.stock <= 0) {
      toast.error('Produto sem estoque');
      return;
    }
    const existing = cart.find(item => item.product_id === product.id);
    if (existing) {
      if (existing.quantity >= product.stock) {
        toast.error('Quantidade máxima atingida');
        return;
      }
      setCart(cart.map(item => 
        item.product_id === product.id 
          ? { ...item, quantity: item.quantity + 1, total: (item.quantity + 1) * item.unit_price }
          : item
      ));
    } else {
      setCart([...cart, {
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        unit_price: product.price,
        total: product.price
      }]);
    }
    setSearchTerm('');
    searchRef.current?.focus();
  };

  // Handler de Enter no campo de busca do PDV (leitor de código de barras)
  const handlePdvSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const term = searchTerm.trim();
      if (!term) return;
      
      // Busca exata por EAN/código de barras primeiro
      const exactMatch = products.find(p => 
        p.ean === term || p.sku === term || p.code === term
      );
      
      if (exactMatch) {
        addToCart(exactMatch);
        return;
      }
      
      // Se tem apenas um resultado, usa ele
      if (filteredProducts.length === 1) {
        addToCart(filteredProducts[0]);
      }
    }
  };

  const updateQuantity = (productId, delta) => {
    const product = products.find(p => p.id === productId);
    setCart(cart.map(item => {
      if (item.product_id === productId) {
        const newQty = Math.max(1, item.quantity + delta);
        if (product && newQty > product.stock) {
          toast.error('Quantidade máxima atingida');
          return item;
        }
        return { ...item, quantity: newQty, total: newQty * item.unit_price };
      }
      return item;
    }));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  const subtotal = cart.reduce((sum, item) => sum + item.total, 0);
  const total = subtotal - discount;
  
  // Para vendas pendentes: o desconto do caixa substitui o desconto original
  // effectiveTotal = subtotal da venda - desconto aplicado no caixa
  const effectiveSubtotal = selectedOrder ? selectedOrder.subtotal : subtotal;
  const effectiveDiscount = discount; // Desconto aplicado no caixa (pode ser 0)
  const effectiveTotal = selectedOrder 
    ? (selectedOrder.subtotal - effectiveDiscount) // Usa subtotal da venda - desconto do caixa
    : total;

  // Funções para múltiplas formas de pagamento
  const addPaymentMethod = () => {
    const amount = parseFloat(currentPaymentAmount);
    if (!amount || amount <= 0) {
      toast.error('Informe um valor válido');
      return;
    }
    
    const totalPaid = paymentsList.reduce((sum, p) => sum + p.amount, 0);
    const remaining = effectiveTotal - totalPaid;
    
    if (amount > remaining + 0.01) {
      toast.error(`Valor máximo: ${formatCurrency(remaining)}`);
      return;
    }
    
    setPaymentsList([...paymentsList, {
      method: currentPaymentMethod,
      amount: amount
    }]);
    
    setCurrentPaymentAmount('');
  };

  const removePayment = (index) => {
    setPaymentsList(paymentsList.filter((_, i) => i !== index));
  };

  const totalPaidMultiple = paymentsList.reduce((sum, p) => sum + p.amount, 0);
  const remainingMultiple = effectiveTotal - totalPaidMultiple;
  
  // Calcula troco apenas se houver pagamento em dinheiro
  const cashPayment = paymentsList.find(p => p.method === 'dinheiro');
  const changeMultiple = totalPaidMultiple > effectiveTotal ? totalPaidMultiple - effectiveTotal : 0;

  const openPaymentDialog = () => {
    setShowPaymentDialog(true);
    setPaymentMethod('dinheiro');
    setAmountPaid('');
    setUseMultiplePayments(false);
    setPaymentsList([]);
    setCurrentPaymentMethod('dinheiro');
    setCurrentPaymentAmount('');
  };

  const finalizeSale = async () => {
    if (cart.length === 0 && !selectedOrder) {
      toast.error('Adicione produtos ao carrinho');
      return;
    }

    setLoading(true);
    try {
      let saleData;
      let response;
      
      // Preparar dados de pagamento
      const mainMethod = useMultiplePayments 
        ? (paymentsList.length > 1 ? 'multiplo' : paymentsList[0]?.method || 'dinheiro')
        : paymentMethod;
      
      const paymentMethodsList = useMultiplePayments 
        ? paymentsList 
        : [{ method: paymentMethod, amount: effectiveTotal }];
      
      const amountPaidValue = useMultiplePayments 
        ? totalPaidMultiple 
        : (paymentMethod === 'dinheiro' ? parseFloat(amountPaid || effectiveTotal) : effectiveTotal);
      
      const changeValue = useMultiplePayments 
        ? changeMultiple 
        : (paymentMethod === 'dinheiro' ? Math.max(0, parseFloat(amountPaid || 0) - effectiveTotal) : 0);

      if (useMultiplePayments) {
        // Validar se o total foi pago
        if (Math.abs(totalPaidMultiple - effectiveTotal) > 0.01 && totalPaidMultiple < effectiveTotal) {
          toast.error('O valor total ainda não foi atingido');
          setLoading(false);
          return;
        }
      }

      // Se é uma VENDA PENDENTE, apenas atualiza ela (não cria nova)
      if (selectedOrder && selectedOrder._type === 'sale') {
        // Atualiza a venda pendente com pagamento e desconto
        response = await axios.post(`${API_URL}/api/pdv/sales/${selectedOrder.id}/checkout`, {
          payment_method: mainMethod,
          payment_methods: paymentMethodsList,
          amount_paid: amountPaidValue,
          change: changeValue,
          discount: effectiveDiscount,
          total: effectiveTotal
        });
        
        setSelectedOrder(null);
        fetchPendingItems();
      } 
      // Se é um PEDIDO, cria venda e marca pedido como finalizado
      else if (selectedOrder && selectedOrder._type === 'order') {
        saleData = {
          client_id: selectedOrder.client_id,
          client_name: selectedOrder.client_name,
          seller_id: selectedSeller?.id || user.id,
          seller_name: selectedSeller?.name || user.name,
          items: selectedOrder.items,
          subtotal: effectiveSubtotal,
          discount: effectiveDiscount,
          total: effectiveTotal,
          payment_method: mainMethod,
          payment_methods: paymentMethodsList,
          amount_paid: amountPaidValue,
          change: changeValue
        };
        
        response = await axios.post(`${API_URL}/api/pdv/sale`, saleData);
        await axios.put(`${API_URL}/api/orders/${selectedOrder.id}/status?status=finalizado`);
        
        setSelectedOrder(null);
        fetchPendingItems();
      }
      // Venda direta no caixa (sem pedido/venda pendente)
      else {
        saleData = {
          client_id: selectedClient?.id || null,
          client_name: selectedClient?.name || 'Consumidor Final',
          seller_id: selectedSeller?.id || user.id,
          seller_name: selectedSeller?.name || user.name,
          items: cart,
          subtotal: subtotal,
          discount: discount,
          total: total,
          payment_method: mainMethod,
          payment_methods: paymentMethodsList,
          amount_paid: amountPaidValue,
          change: changeValue
        };
        
        response = await axios.post(`${API_URL}/api/pdv/sale`, saleData);
      }
      
      setLastSale(response.data);
      setShowPaymentDialog(false);
      setShowDocumentChoiceDialog(true);
      
      // Limpa o carrinho e atualiza dados
      setCart([]);
      setDiscount(0);
      setSelectedClient(null);
      setAmountPaid('');
      setPaymentsList([]);
      setUseMultiplePayments(false);
      fetchData();
      fetchPendingItems();
      checkAndCelebrateDailyGoal();
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao finalizar venda'));
    } finally {
      setLoading(false);
    }
  };

  const handlePrintReceipt = () => {
    setShowDocumentChoiceDialog(false);
    setShowReceiptDialog(true);
    setTimeout(() => window.print(), 500);
  };

  const handleEmitNFCe = async () => {
    if (!nfceConfig) {
      toast.error('NFC-e não configurada. Configure em NFC-e > Configurações');
      return;
    }
    
    setEmittingNfce(true);
    try {
      const response = await axios.post(`${API_URL}/api/nfce/emit`, {
        sale_id: lastSale.id
      });
      
      if (response.data.success) {
        const nfceData = response.data.nfce;
        setLastNfce(nfceData);
        setShowDocumentChoiceDialog(false);
        toast.success(response.data.message || 'NFC-e emitida! Imprimindo...');
        // Imprime automaticamente sem mostrar dialog intermediário
        await handlePrintDANFE(nfceData);
      } else {
        toast.error(response.data.message || 'NFC-e rejeitada pela SEFAZ');
      }
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao emitir NFC-e'));
    } finally {
      setEmittingNfce(false);
    }
  };

  const handleSkipDocument = () => {
    setShowDocumentChoiceDialog(false);
    toast.success('Venda finalizada!');
  };

  const handleSelectPendingItem = (item) => {
    setSelectedOrder(item);
    setShowOrderDialog(false);
    
    // Inicializa o desconto com o valor original da venda (se houver)
    // O operador pode modificar no caixa
    setDiscount(item.discount || 0);
    
    // Se for uma venda pendente, abre direto o pagamento
    if (item._type === 'sale') {
      setShowPaymentDialog(true);
    }
  };

  const openCashMovementDialog = (type) => {
    setMovementType(type);
    setMovementForm({ amount: '', reason: '', notes: '' });
    setShowCashMovementDialog(true);
  };

  const handleCashMovement = async () => {
    if (!movementForm.amount || parseFloat(movementForm.amount) <= 0) {
      toast.error('Informe um valor válido');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/pdv/cash-movement`, {
        type: movementType,
        amount: parseFloat(movementForm.amount),
        reason: movementForm.reason || (movementType === 'sangria' ? 'Sangria de caixa' : 'Suprimento de caixa'),
        notes: movementForm.notes
      });
      toast.success(`${movementType === 'sangria' ? 'Sangria' : 'Suprimento'} registrado com sucesso!`);
      setShowCashMovementDialog(false);
      fetchData();
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao registrar movimentação'));
    } finally {
      setLoading(false);
    }
  };

  const openCashHistoryDialog = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/pdv/cash-movements`);
      setCashMovements(response.data);
      setShowCashHistoryDialog(true);
    } catch (error) {
      toast.error('Erro ao carregar histórico');
    }
  };

  const openEditMovementDialog = (mov) => {
    setEditingMovement(mov);
    setEditMovementForm({
      type: mov.type,
      amount: String(mov.amount),
      reason: mov.reason || '',
      notes: mov.notes || ''
    });
    setShowEditMovementDialog(true);
  };

  const handleEditMovement = async () => {
    if (!editMovementForm.amount || parseFloat(editMovementForm.amount) <= 0) {
      toast.error('Informe um valor válido');
      return;
    }
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/pdv/cash-movement/${editingMovement.id}`, {
        type: editMovementForm.type,
        amount: parseFloat(editMovementForm.amount),
        reason: editMovementForm.reason,
        notes: editMovementForm.notes
      });
      toast.success('Movimentação atualizada com sucesso!');
      setShowEditMovementDialog(false);
      setEditingMovement(null);
      const response = await axios.get(`${API_URL}/api/pdv/cash-movements`);
      setCashMovements(response.data);
      fetchData();
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao editar movimentação'));
    } finally {
      setLoading(false);
    }
  };

  const handlePrintDANFE = async (nfceData) => {
    const nfce = nfceData || lastNfce;
    if (!nfce) {
      toast.error('Não há NFC-e emitida para esta venda');
      return;
    }
    
    try {
      const response = await axios.get(`${API_URL}/api/nfce/${nfce.id}/danfe`, {
        responseType: 'blob'
      });
      
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const errData = JSON.parse(text);
        toast.error(errData.detail || 'Erro ao gerar DANFE');
        return;
      }
      
      // Abre o PDF em iframe oculto e dispara impressão automática
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.top = '-10000px';
      iframe.style.left = '-10000px';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.src = url;
      document.body.appendChild(iframe);
      
      iframe.onload = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (e) {
          // Fallback: abre em nova aba caso iframe não permita impressão
          window.open(url, '_blank');
        }
        // Remove o iframe após um tempo
        setTimeout(() => {
          document.body.removeChild(iframe);
          window.URL.revokeObjectURL(url);
        }, 5000);
      };
      
    } catch (error) {
      const detail = getApiError(error, 'Erro desconhecido');
      toast.error(`Erro ao imprimir DANFE: ${detail}`);
    }
  };

  const paymentMethods = [
    { value: 'dinheiro', label: 'Dinheiro', icon: Banknote },
    { value: 'pix', label: 'PIX', icon: QrCode },
    { value: 'pix_cnpj', label: 'PIX - CNPJ', icon: QrCode },
    { value: 'cartao_debito', label: 'Débito', icon: CreditCard },
    { value: 'cartao_credito', label: 'Crédito', icon: CreditCard },
    { value: 'crediario_bemol', label: 'Crediário Bemol', icon: CreditCard },
    { value: 'vale_mercadoria', label: 'Vale Mercadoria', icon: Receipt },
    { value: 'avancard', label: 'Avancard', icon: CreditCard },
  ];

  const paymentLabels = {
    dinheiro: 'Dinheiro',
    pix: 'PIX',
    pix_cnpj: 'PIX - CNPJ',
    cartao_credito: 'Crédito',
    cartao_debito: 'Débito',
    crediario_bemol: 'Crediário Bemol',
    vale_mercadoria: 'Vale Mercadoria',
    avancard: 'Avancard',
    multiplo: 'Múltiplo'
  };

  return (
    <div className="h-[calc(100vh-7rem)] flex gap-4" data-testid="pdv-page">
      {/* Alerta visual de novo pedido */}
      {newOrderAlert && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 animate-bounce">
          <div className="bg-blue-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3">
            <Bell className="animate-pulse" size={24} />
            <span className="font-semibold text-lg">Novo pedido pendente!</span>
          </div>
        </div>
      )}
      
      {/* Left Panel - Products & Search */}
      <div className="flex-1 flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Receipt className="text-blue-600" />
              Frente de Caixa
            </h1>
            <p className="text-slate-500 text-sm">PDV - Ponto de Venda</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Botão de Som */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={`${soundEnabled ? 'border-blue-500 text-blue-600 bg-blue-50' : 'border-slate-300 text-slate-400'}`}
              title={soundEnabled ? 'Som ativado - Clique para desativar' : 'Som desativado - Clique para ativar'}
              data-testid="btn-toggle-sound"
            >
              {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
            </Button>
            
            {/* Botões de Movimentação */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => openCashMovementDialog('suprimento')}
                className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                data-testid="btn-suprimento"
              >
                <ArrowDownCircle size={16} className="mr-1" />
                Suprimento
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openCashMovementDialog('sangria')}
                className="border-red-500 text-red-600 hover:bg-red-50"
                data-testid="btn-sangria"
              >
                <ArrowUpCircle size={16} className="mr-1" />
                Sangria
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={openCashHistoryDialog}
                className="border-slate-300"
                data-testid="btn-cash-history"
              >
                <History size={16} className="mr-1" />
                Histórico
              </Button>
            </div>
            
            {/* Saldo do Caixa */}
            <div className="flex items-center gap-3 ml-2 pl-3 border-l">
              <div className="text-right">
                <p className="text-xs text-slate-500">Caixa (Dinheiro)</p>
                <p className={`text-lg font-bold ${cashBalance.cash_balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatCurrency(cashBalance.cash_balance)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">Vendas Hoje</p>
                <p className="text-lg font-bold text-blue-600">{formatCurrency(todaySummary.total_sales)}</p>
              </div>
              <Badge className="bg-emerald-100 text-emerald-800 text-lg px-4 py-2">
                {todaySummary.total_count} vendas
              </Badge>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
            <Input
              ref={searchRef}
              type="text"
              placeholder="Buscar por nome, código ou código de barras (F1)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handlePdvSearchKeyDown}
              className="pl-10 h-12"
              data-testid="pdv-search"
            />
          </div>
          <Button 
            variant="outline" 
            className={`h-12 px-4 relative ${pendingItems.length > 0 ? 'border-blue-500 bg-blue-50' : ''}`}
            onClick={() => setShowOrderDialog(true)}
            data-testid="pdv-import-order"
          >
            <Import size={20} className="mr-2" />
            Pedidos/Vendas Pendentes
            {pendingItems.length > 0 && (
              <Badge className="ml-2 bg-blue-600 text-white animate-pulse">
                {pendingItems.length}
              </Badge>
            )}
          </Button>
        </div>

        {/* Products Grid */}
        <div className="flex-1 overflow-auto bg-slate-50 rounded-lg p-4">
          {searchTerm ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {filteredProducts.length > 0 ? (
                filteredProducts.map(product => (
                  <Card 
                    key={product.id}
                    className={`cursor-pointer hover:shadow-md transition-all ${product.stock <= 0 ? 'opacity-50' : ''}`}
                    onClick={() => addToCart(product)}
                    data-testid={`product-card-${product.id}`}
                  >
                    <CardContent className="p-3">
                      <p className="font-medium text-sm truncate">{product.name}</p>
                      <p className="text-xs text-slate-500">{product.code}</p>
                      <div className="flex justify-between items-center mt-2">
                        <p className="text-blue-600 font-bold">{formatCurrency(product.price)}</p>
                        <Badge variant={product.stock > 0 ? "secondary" : "destructive"} className="text-xs">
                          Est: {product.stock}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <p className="text-slate-500 col-span-full text-center py-8">Nenhum produto encontrado</p>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-slate-400">
              <div className="text-center">
                <Search size={48} className="mx-auto mb-2 opacity-50" />
                <p>Digite para buscar produtos</p>
                <p className="text-sm">ou pressione F1 para focar na busca</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Cart */}
      <Card className="w-96 flex flex-col" data-testid="pdv-cart">
        <CardHeader className="pb-2 flex-shrink-0">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <ShoppingCart size={20} />
              Carrinho
              {selectedOrder && (
                <Badge variant="outline" className="ml-2">
                  {selectedOrder._type === 'order' ? `Pedido ${selectedOrder.order_number}` : `Venda ${selectedOrder.sale_number}`}
                </Badge>
              )}
            </CardTitle>
            {cart.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => { setCart([]); setSelectedOrder(null); }}
                data-testid="pdv-clear-cart"
              >
                <Trash2 size={16} className="mr-1" />
                Limpar
              </Button>
            )}
          </div>
          
          {/* Seleção de Vendedor */}
          <div className="mt-2">
            <Label className="text-xs text-slate-500 flex items-center gap-1">
              <UserCheck size={12} />
              Vendedor
            </Label>
            <Select
              value={selectedSeller?.id || ''}
              onValueChange={(value) => {
                const seller = sellers.find(s => s.id === value) || (value === user?.id ? { id: user.id, name: user.name } : null);
                setSelectedSeller(seller);
              }}
            >
              <SelectTrigger className="h-8 text-sm mt-1">
                <SelectValue placeholder="Selecionar vendedor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={user?.id || 'current'}>{user?.name} (Você)</SelectItem>
                {sellers.filter(s => s.id !== user?.id).map(seller => (
                  <SelectItem key={seller.id} value={seller.id}>{seller.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Seleção de Cliente */}
          <div className="mt-2">
            <Label className="text-xs text-slate-500 flex items-center gap-1">
              <User size={12} />
              Cliente
            </Label>
            <Select
              value={selectedClient?.id || 'consumidor_final'}
              onValueChange={(value) => {
                if (value === 'consumidor_final') {
                  setSelectedClient(null);
                } else {
                  setSelectedClient(clients.find(c => c.id === value));
                }
              }}
            >
              <SelectTrigger className="h-8 text-sm mt-1">
                <SelectValue placeholder="Consumidor Final" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="consumidor_final">Consumidor Final</SelectItem>
                {clients.map(client => (
                  <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        
        <CardContent className="flex-1 overflow-auto">
          {selectedOrder ? (
            // Exibe itens do pedido/venda selecionado
            <div className="space-y-2">
              {selectedOrder.items?.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-sm">{item.product_name}</p>
                    <p className="text-xs text-slate-500">{item.quantity}x {formatCurrency(item.unit_price)}</p>
                  </div>
                  <p className="font-semibold text-blue-600">{formatCurrency(item.total)}</p>
                </div>
              ))}
            </div>
          ) : cart.length > 0 ? (
            <div className="space-y-2">
              {cart.map(item => (
                <div key={item.product_id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg" data-testid={`cart-item-${item.product_id}`}>
                  <div className="flex-1">
                    <p className="font-medium text-sm">{item.product_name}</p>
                    <p className="text-xs text-slate-500">{formatCurrency(item.unit_price)} un.</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-7 w-7"
                      onClick={() => updateQuantity(item.product_id, -1)}
                    >
                      <Minus size={14} />
                    </Button>
                    <span className="w-8 text-center font-medium">{item.quantity}</span>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-7 w-7"
                      onClick={() => updateQuantity(item.product_id, 1)}
                    >
                      <Plus size={14} />
                    </Button>
                  </div>
                  <p className="font-semibold text-blue-600 w-20 text-right">{formatCurrency(item.total)}</p>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-7 w-7 text-red-500"
                    onClick={() => removeFromCart(item.product_id)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-slate-400">
              <div className="text-center">
                <ShoppingCart size={48} className="mx-auto mb-2 opacity-50" />
                <p>Carrinho vazio</p>
              </div>
            </div>
          )}
        </CardContent>

        {/* Cart Footer */}
        <div className="border-t p-4 space-y-3 flex-shrink-0 bg-white">
          {/* Seção de Desconto - Aparece para vendas diretas OU vendas pendentes */}
          {(cart.length > 0 || selectedOrder) && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium flex items-center gap-1">
                  <Percent size={14} />
                  Desconto {selectedOrder && <Badge variant="outline" className="ml-1 text-xs">No Caixa</Badge>}
                </Label>
                <div className="flex gap-1">
                  {[5, 10, 15].map((pct) => (
                    <Button
                      key={pct}
                      variant="outline"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={() => {
                        const base = selectedOrder ? selectedOrder.subtotal : subtotal;
                        setDiscount(base * (pct / 100));
                      }}
                      data-testid={`pdv-discount-${pct}pct`}
                    >
                      {pct}%
                    </Button>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-xs text-red-500"
                    onClick={() => setDiscount(0)}
                    data-testid="pdv-discount-clear"
                  >
                    Limpar
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">R$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max={selectedOrder ? selectedOrder.subtotal : subtotal}
                    value={discount || ''}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      const maxDiscount = selectedOrder ? selectedOrder.subtotal : subtotal;
                      setDiscount(Math.min(val, maxDiscount));
                    }}
                    className="pl-9 h-9"
                    placeholder="0,00"
                    data-testid="pdv-discount"
                  />
                </div>
                <span className="text-slate-400">ou</span>
                <div className="flex-1 relative">
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    max="100"
                    value={discount > 0 ? Math.round((discount / (selectedOrder ? selectedOrder.subtotal : subtotal)) * 100) : ''}
                    onChange={(e) => {
                      const pct = parseFloat(e.target.value) || 0;
                      const base = selectedOrder ? selectedOrder.subtotal : subtotal;
                      setDiscount(base * (Math.min(pct, 100) / 100));
                    }}
                    className="pr-8 h-9"
                    placeholder="0"
                    data-testid="pdv-discount-percent"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                </div>
              </div>
            </div>
          )}
          
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span>Subtotal:</span>
              <span>{formatCurrency(effectiveSubtotal)}</span>
            </div>
            {effectiveDiscount > 0 && (
              <div className="flex justify-between text-red-600">
                <span>Desconto:</span>
                <span>-{formatCurrency(effectiveDiscount)}</span>
              </div>
            )}
            <div className="flex justify-between text-xl font-bold">
              <span>Total:</span>
              <span className="text-blue-600">{formatCurrency(effectiveTotal)}</span>
            </div>
          </div>

          <Button 
            className="w-full h-12 text-lg bg-blue-600 hover:bg-blue-700"
            onClick={openPaymentDialog}
            disabled={cart.length === 0 && !selectedOrder}
            data-testid="pdv-pay-btn"
          >
            <Calculator className="mr-2" size={20} />
            Pagamento (F2)
          </Button>
        </div>
      </Card>

      {/* Payment Dialog - Atualizado para múltiplas formas */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Finalizar Pagamento</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500">Total a Pagar</p>
              <p className="text-4xl font-bold text-blue-600">{formatCurrency(effectiveTotal)}</p>
            </div>

            {/* Toggle para múltiplas formas */}
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2">
                <SplitSquareHorizontal className="text-blue-600" size={20} />
                <span className="font-medium text-blue-800">Múltiplas formas de pagamento</span>
              </div>
              <Button
                variant={useMultiplePayments ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  setUseMultiplePayments(!useMultiplePayments);
                  setPaymentsList([]);
                }}
                className={useMultiplePayments ? "bg-blue-600" : ""}
              >
                {useMultiplePayments ? 'Ativado' : 'Ativar'}
              </Button>
            </div>

            {!useMultiplePayments ? (
              // Pagamento simples (forma única)
              <>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Forma de Pagamento</label>
                  <div className="grid grid-cols-2 gap-2">
                    {paymentMethods.map((method) => (
                      <button
                        key={method.value}
                        onClick={() => setPaymentMethod(method.value)}
                        className={`p-3 rounded-lg border flex items-center gap-2 transition-all ${
                          paymentMethod === method.value
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`payment-${method.value}`}
                      >
                        <method.icon size={18} />
                        {method.label}
                      </button>
                    ))}
                  </div>
                </div>

                {paymentMethod === 'dinheiro' && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Valor Recebido</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={amountPaid}
                        onChange={(e) => setAmountPaid(e.target.value)}
                        placeholder="0,00"
                        className="text-2xl h-14 text-right"
                        data-testid="pdv-amount-paid"
                      />
                    </div>
                    {parseFloat(amountPaid || 0) >= effectiveTotal && (
                      <div className="p-4 bg-emerald-50 rounded-lg text-center">
                        <p className="text-sm text-emerald-600">Troco</p>
                        <p className="text-3xl font-bold text-emerald-700">{formatCurrency(parseFloat(amountPaid || 0) - effectiveTotal)}</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              // Múltiplas formas de pagamento
              <div className="space-y-4">
                {/* Lista de pagamentos adicionados */}
                {paymentsList.length > 0 && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Pagamentos adicionados</label>
                    <div className="space-y-2 max-h-32 overflow-auto">
                      {paymentsList.map((payment, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            {paymentMethods.find(m => m.value === payment.method)?.icon && (
                              <span className="text-slate-500">
                                {(() => {
                                  const Icon = paymentMethods.find(m => m.value === payment.method)?.icon;
                                  return Icon ? <Icon size={16} /> : null;
                                })()}
                              </span>
                            )}
                            <span className="font-medium">{paymentLabels[payment.method]}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-blue-600">{formatCurrency(payment.amount)}</span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-red-500"
                              onClick={() => removePayment(idx)}
                            >
                              <X size={14} />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Valor restante */}
                {remainingMultiple > 0.01 && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex justify-between items-center">
                      <span className="text-amber-800">Valor restante:</span>
                      <span className="text-xl font-bold text-amber-700">{formatCurrency(remainingMultiple)}</span>
                    </div>
                  </div>
                )}

                {/* Adicionar nova forma de pagamento */}
                {remainingMultiple > 0.01 && (
                  <div className="space-y-3 p-3 border rounded-lg">
                    <label className="text-sm font-medium">Adicionar pagamento</label>
                    <div className="grid grid-cols-4 gap-2">
                      {paymentMethods.map((method) => (
                        <button
                          key={method.value}
                          onClick={() => setCurrentPaymentMethod(method.value)}
                          className={`p-2 rounded-lg border flex flex-col items-center gap-1 transition-all text-xs ${
                            currentPaymentMethod === method.value
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <method.icon size={16} />
                          {method.label}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        step="0.01"
                        value={currentPaymentAmount}
                        onChange={(e) => setCurrentPaymentAmount(e.target.value)}
                        placeholder={`Valor (máx: ${formatCurrency(remainingMultiple)})`}
                        className="flex-1"
                      />
                      <Button onClick={addPaymentMethod} className="bg-blue-600">
                        <Plus size={18} className="mr-1" />
                        Adicionar
                      </Button>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full"
                      onClick={() => {
                        setCurrentPaymentAmount(remainingMultiple.toFixed(2));
                      }}
                    >
                      Usar valor restante ({formatCurrency(remainingMultiple)})
                    </Button>
                  </div>
                )}

                {/* Troco (se aplicável) */}
                {changeMultiple > 0 && (
                  <div className="p-4 bg-emerald-50 rounded-lg text-center">
                    <p className="text-sm text-emerald-600">Troco</p>
                    <p className="text-3xl font-bold text-emerald-700">{formatCurrency(changeMultiple)}</p>
                  </div>
                )}

                {/* Resumo */}
                {paymentsList.length > 0 && (
                  <div className="p-3 bg-slate-100 rounded-lg">
                    <div className="flex justify-between text-sm">
                      <span>Total informado:</span>
                      <span className="font-bold">{formatCurrency(totalPaidMultiple)}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            <Button 
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700"
              onClick={finalizeSale}
              disabled={
                loading || 
                (!useMultiplePayments && paymentMethod === 'dinheiro' && parseFloat(amountPaid || 0) < effectiveTotal) ||
                (useMultiplePayments && (paymentsList.length === 0 || Math.abs(totalPaidMultiple - effectiveTotal) > 0.01 && totalPaidMultiple < effectiveTotal))
              }
              data-testid="pdv-confirm-sale"
            >
              {loading ? 'Processando...' : 'Confirmar Venda'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Choice Dialog (NFC-e ou Comprovante) */}
      <Dialog open={showDocumentChoiceDialog} onOpenChange={setShowDocumentChoiceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center flex items-center justify-center gap-2">
              <Check className="text-green-600" size={24} />
              Venda Finalizada!
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-center text-slate-600">
              Escolha o tipo de documento para impressão:
            </p>
            
            <div className="grid grid-cols-1 gap-3">
              {/* Opção NFC-e */}
              <Button
                variant="outline"
                className="h-auto p-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-500"
                onClick={handleEmitNFCe}
                disabled={emittingNfce}
              >
                {emittingNfce ? (
                  <Loader2 className="animate-spin text-blue-600" size={32} />
                ) : (
                  <FileCode className="text-blue-600" size={32} />
                )}
                <span className="font-semibold">Emitir NFC-e (DANFE)</span>
                <span className="text-xs text-slate-500">Documento fiscal eletrônico</span>
              </Button>

              {/* Opção Cupom não fiscal */}
              <Button
                variant="outline"
                className="h-auto p-4 flex flex-col items-center gap-2 hover:bg-emerald-50 hover:border-emerald-500"
                onClick={handlePrintReceipt}
              >
                <Receipt className="text-emerald-600" size={32} />
                <span className="font-semibold">Comprovante Não Fiscal</span>
                <span className="text-xs text-slate-500">Cupom simples para controle</span>
              </Button>

              {/* Opção Não imprimir */}
              <Button
                variant="ghost"
                className="text-slate-500"
                onClick={handleSkipDocument}
              >
                Não imprimir documento
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog open={showReceiptDialog} onOpenChange={setShowReceiptDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Cupom da Venda</DialogTitle>
          </DialogHeader>
          {lastSale && <ReceiptPrint sale={lastSale} companyName={companyData.name || user?.company_name || 'EMPRESA'} companyLogo={companyData.logo} />}
          <div className="flex gap-2 mt-4">
            <Button variant="outline" className="flex-1" onClick={() => setShowReceiptDialog(false)}>
              Fechar
            </Button>
            <Button className="flex-1" onClick={() => window.print()}>
              <Printer size={18} className="mr-2" />
              Imprimir
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* DANFE Dialog */}
      <Dialog open={showDANFEDialog} onOpenChange={setShowDANFEDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>DANFE NFC-e</DialogTitle>
          </DialogHeader>
          {lastNfce && lastSale && (
            <DANFEPrint 
              nfce={lastNfce} 
              sale={lastSale} 
              companyData={{ 
                name: user?.company_name, 
                cnpj: nfceConfig?.cnpj,
                address: nfceConfig?.endereco
              }} 
            />
          )}
          <div className="flex gap-2 mt-4">
            <Button variant="outline" className="flex-1" onClick={() => setShowDANFEDialog(false)}>
              Fechar
            </Button>
            <Button className="flex-1" onClick={handlePrintDANFE} data-testid="print-danfe-btn">
              <Printer size={18} className="mr-2" />
              Imprimir
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cash Movement Dialog */}
      <Dialog open={showCashMovementDialog} onOpenChange={setShowCashMovementDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {movementType === 'sangria' ? (
                <><ArrowUpCircle className="text-red-600" /> Sangria de Caixa</>
              ) : (
                <><ArrowDownCircle className="text-emerald-600" /> Suprimento de Caixa</>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg text-center">
              <p className="text-sm text-slate-500">Saldo Atual do Caixa</p>
              <p className={`text-2xl font-bold ${cashBalance.cash_balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {formatCurrency(cashBalance.cash_balance)}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Valor</Label>
              <Input
                type="number"
                step="0.01"
                value={movementForm.amount}
                onChange={(e) => setMovementForm({ ...movementForm, amount: e.target.value })}
                placeholder="0,00"
                className="text-xl h-12"
                data-testid="cash-movement-amount"
              />
            </div>

            <div className="space-y-2">
              <Label>Motivo</Label>
              <Select
                value={movementForm.reason}
                onValueChange={(value) => setMovementForm({ ...movementForm, reason: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o motivo" />
                </SelectTrigger>
                <SelectContent>
                  {movementType === 'sangria' ? (
                    <>
                      <SelectItem value="Sangria de caixa">Sangria de caixa</SelectItem>
                      <SelectItem value="Pagamento de fornecedor">Pagamento de fornecedor</SelectItem>
                      <SelectItem value="Despesa operacional">Despesa operacional</SelectItem>
                      <SelectItem value="Retirada do proprietário">Retirada do proprietário</SelectItem>
                      <SelectItem value="Outro">Outro</SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="Suprimento de caixa">Suprimento de caixa</SelectItem>
                      <SelectItem value="Abertura de caixa">Abertura de caixa</SelectItem>
                      <SelectItem value="Troco inicial">Troco inicial</SelectItem>
                      <SelectItem value="Outro">Outro</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Observações (opcional)</Label>
              <Textarea
                value={movementForm.notes}
                onChange={(e) => setMovementForm({ ...movementForm, notes: e.target.value })}
                placeholder="Observações adicionais..."
                rows={2}
              />
            </div>

            <Button 
              className={`w-full h-12 ${movementType === 'sangria' ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
              onClick={handleCashMovement}
              disabled={loading}
              data-testid="cash-movement-confirm"
            >
              {loading ? 'Processando...' : `Confirmar ${movementType === 'sangria' ? 'Sangria' : 'Suprimento'}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cash History Dialog */}
      <Dialog open={showCashHistoryDialog} onOpenChange={setShowCashHistoryDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="text-blue-600" />
              Histórico de Movimentações
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 overflow-auto">
            {/* Resumo */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="bg-emerald-50 border-emerald-200">
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-emerald-600">Suprimentos</p>
                  <p className="text-2xl font-bold text-emerald-700">{formatCurrency(cashBalance.total_suprimento)}</p>
                </CardContent>
              </Card>
              <Card className="bg-red-50 border-red-200">
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-red-600">Sangrias</p>
                  <p className="text-2xl font-bold text-red-700">{formatCurrency(cashBalance.total_sangria)}</p>
                </CardContent>
              </Card>
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-blue-600">Vendas em Dinheiro</p>
                  <p className="text-2xl font-bold text-blue-700">{formatCurrency(cashBalance.cash_sales)}</p>
                </CardContent>
              </Card>
            </div>

            {/* Lista de movimentações */}
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3">Data/Hora</th>
                    <th className="text-left p-3">Tipo</th>
                    <th className="text-left p-3">Motivo</th>
                    <th className="text-left p-3">Observação</th>
                    <th className="text-right p-3">Valor</th>
                    <th className="text-left p-3">Operador</th>
                    <th className="text-center p-3">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {cashMovements.length > 0 ? (
                    cashMovements.map((mov, idx) => (
                      <tr key={idx} className="border-t hover:bg-slate-50">
                        <td className="p-3">{new Date(mov.created_at).toLocaleString('pt-BR')}</td>
                        <td className="p-3">
                          <Badge className={mov.type === 'suprimento' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}>
                            {mov.type === 'suprimento' ? 'Suprimento' : 'Sangria'}
                          </Badge>
                          {mov.edited && (
                            <span className="block text-[10px] text-amber-600 mt-1" title={`Editado por ${mov.edited_by_name || ''}`}>editado</span>
                          )}
                        </td>
                        <td className="p-3">{mov.reason}</td>
                        <td className="p-3 text-slate-600 text-sm">{mov.notes || '-'}</td>
                        <td className={`p-3 text-right font-medium ${mov.type === 'suprimento' ? 'text-emerald-600' : 'text-red-600'}`}>
                          {mov.type === 'suprimento' ? '+' : '-'}{formatCurrency(mov.amount)}
                        </td>
                        <td className="p-3">{mov.user_name}</td>
                        <td className="p-3 text-center">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-500 hover:text-blue-600"
                            onClick={() => openEditMovementDialog(mov)}
                            title="Editar lançamento"
                            data-testid={`edit-movement-btn-${idx}`}
                          >
                            <Pencil size={15} />
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-slate-500">
                        Nenhuma movimentação registrada hoje
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Cash Movement Dialog */}
      <Dialog open={showEditMovementDialog} onOpenChange={setShowEditMovementDialog}>
        <DialogContent className="max-w-md" data-testid="edit-movement-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="text-blue-600" size={18} />
              Editar Lançamento
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-700">
                Correção de erro de lançamento. A alteração fica registrada para auditoria.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select
                value={editMovementForm.type}
                onValueChange={(value) => setEditMovementForm({ ...editMovementForm, type: value })}
              >
                <SelectTrigger data-testid="edit-movement-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sangria">Sangria</SelectItem>
                  <SelectItem value="suprimento">Suprimento</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Valor</Label>
              <Input
                type="number"
                step="0.01"
                value={editMovementForm.amount}
                onChange={(e) => setEditMovementForm({ ...editMovementForm, amount: e.target.value })}
                placeholder="0,00"
                className="text-xl h-12"
                data-testid="edit-movement-amount"
              />
            </div>

            <div className="space-y-2">
              <Label>Motivo</Label>
              <Input
                value={editMovementForm.reason}
                onChange={(e) => setEditMovementForm({ ...editMovementForm, reason: e.target.value })}
                placeholder="Motivo da movimentação"
                data-testid="edit-movement-reason"
              />
            </div>

            <div className="space-y-2">
              <Label>Observações (opcional)</Label>
              <Textarea
                value={editMovementForm.notes}
                onChange={(e) => setEditMovementForm({ ...editMovementForm, notes: e.target.value })}
                placeholder="Observações adicionais..."
                rows={2}
                data-testid="edit-movement-notes"
              />
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowEditMovementDialog(false)}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                onClick={handleEditMovement}
                disabled={loading}
                data-testid="edit-movement-confirm"
              >
                {loading ? 'Salvando...' : 'Salvar Alteração'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pending Orders/Sales Dialog */}
      <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="text-blue-600" />
              Pedidos e Vendas Pendentes
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                ref={orderSearchRef}
                type="text"
                placeholder="Buscar por número ou cliente..."
                value={orderSearchTerm}
                onChange={(e) => setOrderSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* List */}
            <div className="max-h-96 overflow-auto space-y-2">
              {pendingItems
                .filter(item => 
                  (item.order_number?.toLowerCase().includes(orderSearchTerm.toLowerCase()) ||
                   item.sale_number?.toLowerCase().includes(orderSearchTerm.toLowerCase()) ||
                   item.client_name?.toLowerCase().includes(orderSearchTerm.toLowerCase()))
                )
                .map(item => (
                  <Card 
                    key={item.id}
                    className="cursor-pointer hover:shadow-md transition-all"
                    onClick={() => handleSelectPendingItem(item)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge variant={item._type === 'order' ? 'secondary' : 'outline'}>
                              {item._type === 'order' ? 'Pedido' : 'Venda'}
                            </Badge>
                            <span className="font-bold">
                              {item.order_number || item.sale_number}
                            </span>
                          </div>
                          <p className="text-sm text-slate-500 mt-1">{item.client_name}</p>
                          <p className="text-xs text-slate-400">
                            {new Date(item.created_at).toLocaleString('pt-BR')}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-blue-600">{formatCurrency(item.total)}</p>
                          <p className="text-sm text-slate-500">{item.items?.length || 0} itens</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              {pendingItems.length === 0 && (
                <p className="text-center text-slate-500 py-8">Nenhum pedido ou venda pendente</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PDV;
